/**
 * Production adapter for `remove_small_parts`: the SAME calls the toolbar's
 * Remove small parts panel makes — `SDFOperations.applyRemoveSmallParts` on
 * the structure's texture with the threshold in cm³, then
 * `roi.replaceTexture(result, 'Remove Small Parts')`, a contour-layer view
 * update, and the panel's success snackbar with the removed / total part
 * counts (same message ID) — shared in {@link makeInPlaceEngineDeps}. So the agent
 * overwrites the structure exactly as the panel does, with the same undo
 * step. The engine is a SYSTEM BOUNDARY: this glue is verified by the manual
 * demo; the logic it feeds is unit-tested through fakes.
 */
import { useCallback } from 'react';
import { useIntl } from 'react-intl';
import { useRecoilValue } from 'recoil';
import { SDFOperations } from '~/engine/util/algorithms/sdf/SDFOperations';
import { environmentState } from '~/state/atoms/engineAtoms';
import { currentStructureSetState } from '~/state/atoms/structureSet/structureSetAtoms';
import {
    makeInPlaceEngineDeps,
    requireSourceTexture,
    toComputedGeometry,
} from '../core';
import type {
    RemoveSmallPartsComputed,
    RemoveSmallPartsDeps,
} from './toolRunner';

export function useRemoveSmallPartsEngineDeps(): () => Omit<
    RemoveSmallPartsDeps,
    'pendingMutationExists'
> {
    const env = useRecoilValue(environmentState);
    const structureSet = useRecoilValue(currentStructureSetState);
    const intl = useIntl();

    return useCallback(() => {
        const base = makeInPlaceEngineDeps(
            env,
            structureSet,
            'Remove Small Parts',
            (name, computed) => {
                const parts = computed as RemoveSmallPartsComputed;
                return intl.formatMessage(
                    { id: 'postProcessing.removeSmallParts.success' },
                    {
                        name,
                        removed: parts.partsRemoved,
                        total: parts.partsFound,
                    }
                );
            }
        );
        return {
            ...base,
            geometry: {
                ...base.geometry,
                async applyRemoveSmallParts(
                    roiNumber: number,
                    minVolumeCm3: number
                ): Promise<RemoveSmallPartsComputed> {
                    const src = requireSourceTexture(env, roiNumber);
                    const result = await SDFOperations.applyRemoveSmallParts(
                        src,
                        minVolumeCm3
                    );
                    // undefined = another contour operation holds the GL
                    // guard, or the source had no readable SDF data.
                    if (!result) {
                        throw new Error(
                            'remove small parts could not run (another ' +
                                'contour operation may be in progress); ' +
                                'try again'
                        );
                    }
                    const geometry = toComputedGeometry(result.texture);
                    return {
                        handle: geometry.handle,
                        // Keep the volume lazy, like toComputedGeometry.
                        get resultVolumeCm3(): number {
                            return geometry.resultVolumeCm3;
                        },
                        partsFound: result.numComponentsFound,
                        partsRemoved: result.numComponentsRemoved,
                    };
                },
            },
        };
    }, [env, structureSet, intl]);
}
